import { useState } from 'react';
import { observer } from 'mobx-react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CategoryItem } from './components/CategoryItem/CategoryItem';
import CustomModalComponent from 'src/view/components/Controls/Modal/Modal';
import ModalBackground from 'src/view/components/Controls/Modal/ModalBackground/ModalBackground';
import ModalHeader from 'src/view/components/Controls/Modal/ModalHeader/ModalHeader';
import { dndAutoScrollOptionsDefault, dndOverlayAnimationDefault, DndSensors } from 'src/utils/dnd';
import ScrollableBlock from 'src/view/containers/ScrollableBlock/ScrollableBlock';
import { modalStore } from 'src/mobx/modalStore';
import categoriesStore from 'src/view/screens/MenuManagementScreen/screens/CategoriesScreen/store/categoriesStore';
import Portal from 'src/view/components/Portal/Portal';
import { localeStore } from 'src/mobx/localesStore';
import { getTranslate } from 'src/utils/helpers';
import { TCategoryProduct } from 'src/types/categories';
import LoadingOverlay from 'src/view/components/LoadingOverlay/LoadingOverlay';

export const CategoryItemsModal = observer((): JSX.Element => {
  const [sortedItems, setSortedItems] = useState<TCategoryProduct[] | undefined>(modalStore.categoryItemsModal?.items);
  const [draggableItem, setDraggableItem] = useState<TCategoryProduct | null>(null);

  const changeItemsSorting = async (swapItemId: number) => {
    if (!modalStore.categoryItemsModal?.category || !draggableItem) return;
    const _sortedItems = await categoriesStore.updateCategoryItemsSorting(modalStore.categoryItemsModal?.category.id, {
      id: draggableItem?.id,
      sort: swapItemId,
    });
    setSortedItems(_sortedItems);
  };

  const onDragItem = (index: number | null) => {
    const _selectedItem = sortedItems?.find((...[, _index]) => _index === index);
    setDraggableItem(_selectedItem || null);
  };

  const handleDragEnd = async (swapItemId?: string) => {
    if (swapItemId !== undefined) await changeItemsSorting(Number(swapItemId) - 1);
    onDragItem(null);
  };

  return (
    <CustomModalComponent isOpen onRequestClose={modalStore.closeCategoryItemsModal} modalMaxWidth={546} withCloseIcon>
      <ModalBackground>
        <ModalHeader title={localeStore.t('items_in') + ' ' + getTranslate(modalStore.categoryItemsModal?.category.name)} />
        <div className="position-relative d-flex flex-1 flex-column pt-4 px-6 pb-5">
          <DndContext
            id="items"
            autoScroll={dndAutoScrollOptionsDefault}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            sensors={DndSensors()}
            onDragStart={({ active }) => onDragItem(Number(active.id) - 1)}
            onDragCancel={() => onDragItem(null)}
            onDragEnd={({ over }) => handleDragEnd(over?.id)}>
            <ScrollableBlock className="d-flex flex-1 flex-column" showScrollbar scrollbarSize="small">
              {sortedItems?.map((item, index) => (
                <CategoryItem key={item.id} index={index + 1} item={item} />
              ))}
            </ScrollableBlock>

            <Portal>
              <DragOverlay zIndex={1001} dropAnimation={dndOverlayAnimationDefault}>
                {draggableItem ? <CategoryItem key={0} index={0} item={draggableItem} className="category-item__drag-active" /> : null}
              </DragOverlay>
            </Portal>
          </DndContext>

          <LoadingOverlay visible={categoriesStore.isLoading} refreshing />
        </div>
      </ModalBackground>
    </CustomModalComponent>
  );
});
